import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useMergedRefs from '@restart/hooks/useMergedRefs';
import clsx from 'clsx';
import React, {MouseEventHandler} from 'react';
import type {ButtonProps} from '../Button/Button';
import {Button} from '../Button/Button';
import {IconButton} from '../Button/IconButton';
import {dropdownCssClassNames} from './dropdownCssClassNames';
import {useDropdownContext} from './hooks/useDropdownContext';

export interface DropdownButtonProps extends ButtonProps {
    /**
     * Used within navigation to indicate
     * active element
     */
    selected?: boolean;
    /**
     * Used to replace root node with different
     * component.
     */
    component?: React.ElementType<any>;
}

export const DropdownButton = React.forwardRef<any, DropdownButtonProps>(({
    children, className, component: Component = Button, endIcon = <KeyboardArrowDownIcon/>, onClick,
    ...props
}, ref) => {
    const {
        open,
        handleToggle,
        anchorRef,
        dropdownMenuId
    } = useDropdownContext('Button');

    const mergedRef = useMergedRefs(anchorRef, ref);

    const handleClick: MouseEventHandler<HTMLButtonElement> = (event): void => {
        handleToggle();
        onClick?.(event);
    };

    return (
        <Component
            aria-controls={open ? dropdownMenuId : undefined}
            aria-expanded={open}
            aria-haspopup='true'
            className={clsx(
                dropdownCssClassNames.button,
                className
            )}
            {...(Component !== IconButton && {
                endIcon
            })}
            onClick={handleClick}
            ref={mergedRef}
            {...props}
        >
            {children}
        </Component>
    );
});

DropdownButton.displayName = 'DropdownButton';
